import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, CircularProgress, Grid, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { EntryEditor } from '../../components/journals/EntryEditor';
import { FeedbackPanel } from '../../components/journals/FeedbackPanel';
import { useJournals } from '../../hooks/useJournals';
import { useJournalEntry, useCreateEntry, useUpdateEntry } from '../../hooks/useJournalEntries';
import { useRequestReview } from '../../hooks/useJournalReview';
import { useSubscriptionStatus } from '../../hooks/useSubscription';
import { getRandomPromptPair } from '../../data/journalPrompts';

export const JournalEditorPage = () => {
  const { journalId, entryId } = useParams<{ journalId: string; entryId: string }>();
  const navigate = useNavigate();
  const isNew = entryId === undefined || entryId === 'new';

  const { data: journals } = useJournals();
  const journal = journals?.find(j => j.id === journalId);

  const { data: entryDetail, isLoading: entryLoading } = useJournalEntry(
    journalId!,
    isNew ? '' : entryId!
  );

  const { data: subscriptionStatus, isLoading: subLoading } = useSubscriptionStatus();
  // Default to showing the button while loading — the backend enforces the real gate (HTTP 402).
  // Only show ProGate when we have a confirmed free tier.
  const isPro = subLoading || !subscriptionStatus
    ? true
    : ['basic', 'premium', 'pro'].includes(subscriptionStatus.tier);

  const { mutate: createEntry, isPending: isCreating } = useCreateEntry(journalId!);
  const { mutate: updateEntry, isPending: isUpdating } = useUpdateEntry(journalId!, isNew ? '' : entryId!);
  const { mutate: requestReview, isPending: isReviewing } = useRequestReview(journalId!, isNew ? '' : entryId!);

  const promptPair = useMemo(
    () => getRandomPromptPair(journal?.learningLanguage, journal?.nativeLanguage),
    [journal?.learningLanguage, journal?.nativeLanguage],
  );

  const handleSave = (title: string | undefined, body: string) => {
    if (isNew || !entryDetail) {
      createEntry(
        { title, body, dailyPrompt: promptPair.learning },
        {
          onSuccess: (entry) => {
            navigate(`/journals/${journalId}/entries/${entry.id}`, { replace: true });
          },
        }
      );
    } else {
      updateEntry({ title, body });
    }
  };

  if (entryLoading && !isNew) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const feedback = entryDetail?.latestFeedback ?? null;

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Tooltip title="Back to entries">
          <IconButton onClick={() => navigate(`/journals/${journalId}`)}><BackIcon /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3} sx={{ flex: 1, overflow: 'hidden' }}>
        <Grid size={{ xs: 12, md: 7 }} sx={{ height: '100%', overflowY: 'auto' }}>
          <EntryEditor
            initialTitle={entryDetail?.title}
            initialBody={entryDetail?.body ?? ''}
            prompt={promptPair.learning}
            nativePrompt={promptPair.native}
            learningLanguage={journal?.learningLanguage ?? 'target language'}
            nativeLanguage={journal?.nativeLanguage ?? 'native language'}
            onSave={handleSave}
            isSaving={isCreating || isUpdating}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }} sx={{ height: '100%', overflowY: 'auto' }}>
          <FeedbackPanel
            isPro={isPro}
            feedback={feedback}
            onRequestReview={() => requestReview()}
            isLoading={isReviewing}
            entryHasBody={!!(entryDetail?.body?.trim())}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
